import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ActiveCartService } from '@spartacus/cart/base/core';
import {
  Address,
  ADDRESS_NORMALIZER,
  ADDRESS_SERIALIZER,
  ADDRESS_VALIDATION_NORMALIZER,
  AddressValidation,
  ConverterService,
  InterceptorUtil,
  OCC_USER_ID_ANONYMOUS,
  OccEndpointsService,
  OccUserAddressAdapter,
  USE_CLIENT_TOKEN,
  UserIdService
} from '@spartacus/core';
import { combineLatest, Observable, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, take } from 'rxjs/operators';

import { DpOccAddressList } from '@app/core/types/backend/dp-occ-address-list.interface';
import { DpOccAddress } from '@app/core/types/backend/dp-occ-address.interface';

// TODO: remove disabled eslint rules
@Injectable({ providedIn: 'root' })
export class DpOccUserAddressAdapter extends OccUserAddressAdapter {
  constructor(
    http: HttpClient,
    occEndpoints: OccEndpointsService,
    converter: ConverterService,
    private readonly activeCartService: ActiveCartService,
    private readonly userIdService: UserIdService
  ) {
    super(http, occEndpoints, converter);
  }

  loadAll(userIdParam: string): Observable<Address[]> {
    return combineLatest([this.userIdService.getUserId(), this.activeCartService.getActiveCartId()]).pipe(
      take(1),
      filter(([userId, cartId]) => !!userId && !!cartId),
      mergeMap(([userId, cartId]) => {
        const url = this.occEndpoints.buildUrl('orgUsers/${userId}/carts/${cartId}/addresses/shipping?fields=FULL', {
          urlParams: {
            userId: userIdParam ?? userId,
            cartId
          }
        });

        const headers = new HttpHeaders({
          'Content-Type': 'application/json'
        });

        return this.http.get<DpOccAddressList>(url, { headers }).pipe(
          catchError((error: unknown) => throwError(() => error)),
          map((addressList) => {
            if (addressList?.addresses?.length) {
              return addressList.addresses?.map((address) => ({
                ...address,
                editable: addressList.editable
              })) as DpOccAddress[];
            }

            return [{ editable: addressList.editable }] as DpOccAddress[];
          }),
          this.converter.pipeableMany(ADDRESS_NORMALIZER)
        );
      })
    );
  }

  add(userId: string, address: DpOccAddress): Observable<object> {
    const url = this.occEndpoints.buildUrl('users/${userId}/addresses/addShipping', {
      urlParams: { userId }
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    address = this.converter.convert(address, ADDRESS_SERIALIZER);

    return this.http.post(url, address, { headers }).pipe(catchError((error: unknown) => throwError(() => error)));
  }

  update(userId: string, addressId: string, address: DpOccAddress): Observable<object> {
    const url = this.occEndpoints.buildUrl('addressDetail', {
      urlParams: { userId, addressId }
    });
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    address = this.converter.convert(address, ADDRESS_SERIALIZER);

    return this.http.patch(url, address, { headers }).pipe(catchError((error: unknown) => throwError(() => error)));
  }

  verify(userId: string, address: DpOccAddress): Observable<AddressValidation> {
    const url = this.occEndpoints.buildUrl('addressVerification', {
      urlParams: {
        userId
      }
    });

    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (userId === OCC_USER_ID_ANONYMOUS) {
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
    }

    address = this.converter.convert(address, ADDRESS_SERIALIZER);

    return this.http.post<AddressValidation>(url, address, { headers }).pipe(
      catchError((error: unknown) => throwError(() => error)),
      this.converter.pipeable(ADDRESS_VALIDATION_NORMALIZER)
    );
  }
}
